/**
 * Open-template service — `openTemplateInExcel(key)`.
 *
 * UI-free implementation of the FactSet template-open chain.
 * Today's only consumer is the Excel chat assistant (when the user
 * asks "open the public company profile template" — see
 * `excelAIService.ts::classifyQueryIntent` and
 * `excel/ExcelApp.tsx::handleQuery`). The function is intentionally
 * UI-agnostic so future surfaces (slash command, MCP tool, chart-
 * bubble action, etc.) can drive the same chain without re-
 * implementing the parallel orchestration.
 *
 * What the chain does (success path; see the per-step comments below
 * for the full rationale, including why we split AddWorkbook out of
 * `InsertWorksheetsFromBase64({ addWorkbook: { enabled: true, name } })`):
 *
 *   [serial]   `waitForHostReady` (top gate — abort if Excel is mid-cell-edit or busy)
 *   [parallel] `factset.DownloadTemplate`
 *            ‖ `factset.Spreadsheet.SetCalculationMode("manual")`
 *   [serial]   `factset.Spreadsheet.AddWorkbook` (only when opening into a new workbook)
 *   [serial]   `waitForHostReady` (pre-insert gate — covers the user entering edit mode during the ~3.5s download)
 *   [serial]   `factset.Spreadsheet.InsertWorksheetsFromBase64`  (no `addWorkbook` field)
 *   [parallel] `factset.Spreadsheet.ActivateWorksheet(sheetNames[0])`
 *            ‖ `factset.Spreadsheet.DeleteWorksheet("Sheet1")`     (new-workbook cleanup only)
 *   [serial]   `factset.Spreadsheet.SetCalculationMode("automatic")`
 *   [serial]   `factset.Spreadsheet.HarmonizedRefreshWorksheets`   (default-on; FDS-data fetch on every imported sheet)
 *
 * **Host-ready gate.** Both gate steps probe `GetActiveExcelContext`'s
 * `isInEditMode` / `isBusy` flags on the shared host-ready schedule
 * and abort with
 * `OpenTemplateError("hostReady:stuck:edit"|"hostReady:stuck:busy", ...)`
 * if the host is still stuck. Without this, structural intents like
 * `SetCalculationMode` and `InsertWorksheetsFromBase64` reject mid-
 * chain with `InvalidOperationInCellEditMode` and the user sees the
 * generic "while opening the template" copy with no actionable hint.
 * Shared with `excelService.ts::applyWorkbookModificationsViaIntents`
* via the `services/hostReady.ts` module.
 *
 * The trailing refresh step is the difference between "template is
 * open" and "template is open AND populated". Without it the
 * workbook lands with FactSet formulas visible but data unfetched
 * — Excel's `SetCalculationMode("automatic")` only kicks Excel's
 * native calc engine; FDS / FDSC / FDSZ formulas need the FactSet
 * runtime's own refresh to fetch their values. We pass an explicit
 * `sheetNames` list (every sheet returned by
 * `InsertWorksheetsFromBase64`) instead of letting the wrapper
 * send `fdc3.nothing` — per FactSet's `excel-tools.md` the latter
 * only refreshes the currently selected sheet(s), leaving every
 * other tab stale until the user activates it and hits refresh
 * manually.
 * Callers who want to defer the refresh (e.g. to queue
 * `RefreshCalculate` with custom inputs instead) can pass
 * `options.refresh: false`.
 *
 * The refresh runs best-effort with a tri-state outcome on
 * `OpenTemplateResult.refresh`: `"completed"` if the host returned
 * a success ack within `REFRESH_TIMEOUT_MS`, `"in_progress"` if
 * our soft timeout fired (host RPC still running in the background;
 * the host's own progress dialog is the user-visible source of
 * truth for completion), `"failed"` on a non-timeout host error,
 * `"skipped"` if `options.refresh: false`. The discriminator lets
 * the chat handler differentiate "host still working — be patient"
 * from "host returned an error — try refresh manually" instead of
 * collapsing both into a single misleading warning.
 *
 * The two parallel pairs overlap host-RPC overhead for ~3.4s of
 * wall-clock saving on a typical (~14s) open. Each parallel pair is
 * provably safe to fire concurrently — see the per-step comments for
 * the convergence argument.
 *
 * The function throws `OpenTemplateError` on hard failures (download
 * empty / addWorkbook crash / insert crash) — the cosmetic cleanup
 * steps (activate cover, delete leftover Sheet1) catch and stamp
 * their own errors so a sheet-name collision can't derail an
 * otherwise-successful open.
 *
 * **Observation hook.** Callers wire `options.onPhase` to mirror
 * timing into their own surface (the chat handler's `loadingStatus`
 * line, telemetry, etc.). The hook is fire-and-forget — exceptions
 * thrown by the caller's `onPhase` are swallowed so a buggy
 * observer can't break the open.
 */

import {
  downloadTemplate,
  refreshCalculate,
  awaitRefreshCalculate,
  getRefresh,
  insertWorksheetsFromBase64,
  addWorkbook,
  activateWorksheet,
  deleteWorksheet,
  setCalculationMode,
  getCalculationMode,
  getActiveExcelContext,
  getWorkbookNamedObjects,
  removeNamedObject,
  refreshWorksheets,
} from "../excelHostOps";
import type { CalculationMode } from "./factset/spreadsheetIntents";
import type {
  FactsetDownloadTemplate,
  FactsetRefreshCalculateInputs,
} from "../types";
import {
  waitForHostReady,
  waitForHostReadyPatient,
  drainEditModeAfterWrite,
  hostReadyRetryCopy,
} from "./hostReady";
import type {
  HostReadyReason,
  HostReadyResult,
  PatientHostReadyResult,
} from "./hostReady";
import { isIntentTimeoutError } from "../../shared/factset/factsetIntentTransport";

/**
 * Soft timeout (ms) for the post-open `harmonizedRefreshWorksheets`
 * call. Long enough to cover small / medium templates end-to-end so
 * the chain can report `refresh: "completed"` accurately, but short
 * enough that large templates (thousands of FDS formulas, multi-
 * minute fetches) bucket into `refresh: "in_progress"` without
 * hanging the open chain — the host has its own progress dialog
 * which is the user-visible source of truth for completion. The
 * underlying RPC is NOT cancelled by our timeout: per
 * `factsetIntentTransport.ts`'s `Timed out after Nms` path, the
 * host work continues regardless of whether we're still listening,
 * so the user's data still lands; we just stop awaiting the ack.
 */
const REFRESH_TIMEOUT_MS = 30_000;
// Template imports can involve multiple worksheets, workbook-level names,
// styles, formulas, and host-side dependency repair. The shared 15s intent
// timeout is too short for DCF/LBO templates on a warm workbook.
const INSERT_WORKSHEETS_TIMEOUT_MS = 5 * 60_000;
const TEMPLATE_BUSY_HOST_READY_SCHEDULE: ReadonlyArray<number> = [1_000, 2_000, 4_000];
const TEMPLATE_IDENTIFIER_NAME = "Identifier";

function namedObjectName(namedObject: unknown): string | undefined {
  if (!namedObject || typeof namedObject !== "object") return undefined;
  const obj = namedObject as Record<string, unknown>;
  const name = obj.name ?? obj.Name;
  return typeof name === "string" ? name : undefined;
}

async function workbookHasNamedObject(
  workbookName: string,
  name: string
): Promise<boolean> {
  const normalizedName = name.toLowerCase();
  const existingNames = await getWorkbookNamedObjects({
    workbookName,
    skipHidden: false,
  });
  return existingNames.some(
    (namedObject) => namedObjectName(namedObject)?.toLowerCase() === normalizedName
  );
}

/**
 * Phase labels emitted to `OpenTemplateOptions.onPhase`. Used by the
 * chat handler (`ExcelApp.tsx::handleQuery`) to drive `loadingStatus`
 * updates on the user-visible chat row. Append-only — adding new
 * phases is fine; renaming existing ones means every consumer has
 * to update its label map at the same time.
 */
export type OpenTemplatePhase =
  | "hostReady:stuck:edit"
  | "hostReady:stuck:busy"
  | "hostReady:waiting:edit"
  | "hostReady:waiting:busy"
  | "hostReady:cleared"
  | "download:start"
  | "download:end"
  | "download:error"
  | "refreshCalculate:start"
  | "refreshCalculate:poll"
  | "refreshCalculate:end"
  | "getRefresh:end"
  | "setCalc:read:end"
  | "setCalc:read:error"
  | "setCalc:manual:start"
  | "setCalc:manual:end"
  | "setCalc:manual:error"
  | "setCalc:manual:skipped"
  | "addWorkbook:start"
  | "addWorkbook:end"
  | "addWorkbook:error"
  | "insert:start"
  | "insert:end"
  | "insert:error"
  | "activate:cover:start"
  | "activate:cover:end"
  | "activate:cover:error"
  | "delete:sheet1:start"
  | "delete:sheet1:end"
  | "delete:sheet1:error"
  | "delete:sheet1:skipped"
  | "setCalc:auto:start"
  | "setCalc:auto:end"
  | "setCalc:auto:error"
  | "refresh:start"
  | "refresh:end"
  | "refresh:error"
  | "refresh:in-progress"
  | "refresh:skipped";

export interface OpenTemplateResult {
  /** Names of imported sheets, in host order. `sheetNames[0]` is the
   *  one we explicitly activated as the landing tab (typically the
   *  template's Cover). May be empty if the host returned no names. */
  sheetNames: string[];
  /** Decoded byte size of the `.xlsx` payload, in KB. Undefined if
   *  base64 was malformed and `decodeBase64ByteLength` rejected (the
   *  open continued anyway and let the host's `InsertWorksheetsFromBase64`
   *  return its own verdict on the bytes). */
  bytesKb?: number;
  /** Whether the manual-calc bracket actually engaged. False means
   *  `SetCalculationMode("manual")` failed before the import; the
   *  open still ran but without the recalc-storm safety net, so a
   *  caller surfacing this can flag "ran without bracket". */
  calcModeFlipped: boolean;
  /** Wall-clock duration of the whole chain, including parallel
   *  overlaps and the post-open refresh step. */
  durationMs: number;
  /** Raw `DownloadTemplate` envelope, with the heavy `workbookBase64`
   *  blob preserved. Callers that want to display this should elide
   *  the base64 themselves to keep the response readable. */
  downloadResult: FactsetDownloadTemplate;
  /** Outcome of the post-open `harmonizedRefreshWorksheets` step:
   *
   *   - `"completed"` — the host returned a success ack within
   *     `REFRESH_TIMEOUT_MS`; FDS data is live on every imported
   *     sheet.
   *   - `"in_progress"` — our soft timeout fired but the host RPC
   *     is still in flight. The host's progress dialog is the
   *     user-visible source of truth for completion; common on
   *     large templates (thousands of FDS formulas) where the
   *     fetch takes minutes. Treat as success-with-caveat in chat.
   *   - `"failed"` — the host returned a non-timeout error or the
   *     transport rejected. The user may need to refresh manually.
   *   - `"skipped"` — caller opted out via `options.refresh: false`,
   *     or the chain failed before reaching the refresh step.
   *
   * Replaces the previous `refreshed: boolean` so the chat handler
   * can differentiate "host still working" (do not warn) from
   * "host returned an error" (warn, suggest manual refresh).
   */
  refresh: "completed" | "in_progress" | "failed" | "skipped";
  /** True when bytes came from RefreshCalculate with caller-supplied inputs. */
  instantiatedWithInputs?: boolean;
  /** Where the template sheets were inserted. */
  targetWorkbook: "current" | "new";
  /** Set when a current-workbook request was moved to a new workbook to avoid a host modal. */
  retargetedFrom?: "current";
}

export interface OpenTemplateOptions {
  /**
   * Fired at every phase boundary (start / end / error) for caller-
   * side observation: chat `loadingStatus` updates, telemetry, etc.
   * Free-form `data` payload per phase — see the per-call sites
   * below for the shapes. Throws here are swallowed by the service
   * so caller bugs can't derail the open chain.
   */
  onPhase?: (phase: OpenTemplatePhase, data?: unknown) => void;
  /**
   * Whether to run `harmonizedRefreshWorksheets` after the workbook
   * is open. Default `true` — without it the new workbook lands
   * with FactSet formulas visible but data unfetched (cells show
   * the formula's last-cached value or `#N/A` until the user hits
   * the host's manual refresh button). Set to `false` for callers
   * that want to do their own refresh later (e.g. queue a refresh
   * with custom inputs via `refreshCalculate`).
   */
  refresh?: boolean;
  /**
   * Template input values keyed by host inputType (e.g.
   * `main_identifier`). When present, the service asks
   * `factset.RefreshCalculate` for an instantiated workbook instead of
   * downloading the template's default/sample workbook.
   */
  inputs?: FactsetRefreshCalculateInputs;
  /**
   * Where to insert the downloaded / instantiated template workbook.
   * Defaults to the active workbook; pass `"new"` only when the user
   * explicitly asked for a separate workbook/file.
   */
  targetWorkbook?: "current" | "new";
  /**
   * What to do when importing into the current workbook would collide with
   * an existing workbook-level `Identifier` name. Default stays conservative:
   * retarget to a new workbook so Excel never shows its native duplicate-name
   * modal. `"replaceExisting"` is only for an explicit user confirmation.
   */
  identifierConflictAction?: "retargetToNewWorkbook" | "replaceExisting";
  /**
   * AbortSignal observed by long-running waits (currently the patient
   * host-ready gate). When aborted mid-wait, the open chain unwinds
   * with a `DOMException("AbortError")` so the chat handler can route
   * to its standard "🛑 Run cancelled" message. The actual host
   * write primitives don't observe this signal (they're fire-and-
   * forget RPCs the host completes regardless), so a cancel during
   * `InsertWorksheetsFromBase64` won't interrupt the import — only
   * before-the-write waits respect it.
   */
  signal?: AbortSignal;
}

/**
 * Partial state captured at the point a hard-step failure occurred.
 * Lets callers render step-aware error messages with the same
 * enrichment ("241 KB · calc bracket: manual") they'd surface on a
 * successful open, instead of falling back to a bare error string.
 */
export interface OpenTemplatePartialState {
  /** Set once `DownloadTemplate` has resolved with non-empty bytes
   *  (i.e. on `addWorkbook:error` / `insert:error`). Undefined on
   *  the two `download:error` paths (network throw / empty body). */
  downloadResult?: FactsetDownloadTemplate;
  /** Decoded size of the downloaded `.xlsx`, in KB. Same set as
   *  `downloadResult` modulo malformed-base64 cases. */
  bytesKb?: number;
  /** Whether the manual-calc bracket engaged before the failure
   *  point. False on `download:error` paths (we await `setCalc`
   *  before throwing but the bracket may not have engaged). */
  calcModeFlipped?: boolean;
}

/**
 * Thrown by `openTemplateInExcel` on a hard step failure (one of
 * the three critical steps: download / addWorkbook / insert).
 * Carries the offending `phase` so callers can render a step-aware
 * message without parsing the human-readable `message`, and a
 * `partial` snapshot of state captured up to the failure point so
 * the same enrichment fields surface on error rows as on success
 * rows.
 *
 * Cleanup-step failures (activate cover / delete leftover Sheet1)
 * do NOT throw — those are best-effort and only emit `*:error`
 * phases via `onPhase`.
 */
export class OpenTemplateError extends Error {
  constructor(
    public readonly phase: OpenTemplatePhase,
    message: string,
    public readonly cause?: unknown,
    public readonly partial: OpenTemplatePartialState = {}
  ) {
    super(message);
    this.name = "OpenTemplateError";
    // Restore the prototype chain. Under the ES5 compile target,
    // `super(message)` resets `this`'s prototype to `Error.prototype`,
    // which breaks `instanceof OpenTemplateError` at every callsite and
    // makes `err.constructor.name` report `"Error"`. The chat-side
    // error renderer relies on `instanceof` to extract `phase`, so
    // without this line a documented throw surfaces as
    // `Template open failed at "unknown" (Error)` and the engineer
    // loses the step label that was supposed to localise the failure.
    Object.setPrototypeOf(this, OpenTemplateError.prototype);
  }
}

/**
 * Map a `waitForHostReady` reason into the corresponding
 * `OpenTemplatePhase` + user-facing message pair. Returns `null` for
 * non-stuck reasons (`ready`, `cleared`, `probe_failed`) so the caller
 * can branch with a single null check.
 *
 * Centralised here so both gate sites (`top`, `pre-insert`) emit the
 * same phase string + same actionable copy; drift between the two
 * would surface as two different chat messages for the same root
 * cause. The open-template flow uses a longer patient wait than the
 * shared `EDIT_MODE_USER_MESSAGE` assumes, so we override the copy
 * here to acknowledge that we already waited — the user gets a
 * different read on "you were too slow" vs "Excel hiccupped".
 */
function mapStuckReasonToPhaseAndMessage(
  reason: HostReadyReason
): { phase: OpenTemplatePhase; message: string } | null {
  // The patient gate in `waitForTemplateHostReadyPatient` (below) has
  // already polled for up to 5 minutes before the host was declared stuck,
  // so `retried: true` is the honest flag to pass — it acknowledges
  // the wait in the copy ("I waited and retried automatically, but
  // Excel still was not ready…") instead of implying the user was
  // slow. `retryAction: false` because the open-template chain doesn't
  // surface its own Retry button — the user re-asks via chat.
  if (reason === "stuck_edit") {
    return {
      phase: "hostReady:stuck:edit",
      message: hostReadyRetryCopy(reason, {
        retried: true,
        retryAction: false,
      }).messageOneLiner,
    };
  }
  if (reason === "stuck_busy") {
    return {
      phase: "hostReady:stuck:busy",
      message: hostReadyRetryCopy(reason, {
        retried: true,
        retryAction: false,
      }).messageOneLiner,
    };
  }
  return null;
}

async function waitForTemplateHostReady(): Promise<HostReadyResult> {
  const first = await waitForHostReady();
  if (first.reason !== "stuck_busy") {
    return first;
  }

  const second = await waitForHostReady(TEMPLATE_BUSY_HOST_READY_SCHEDULE);
  return {
    ...second,
    attempts: first.attempts + second.attempts,
  };
}

/**
 * Patient template-open host-ready gate. Runs the short
 * `waitForTemplateHostReady` first (absorbs transient busy /
 * sub-second cell-edit blips with no UX cost), and if the host is
 * still stuck, escalates to `waitForHostReadyPatient` so the user
 * has up to 5 minutes to commit/cancel their cell edit or wait out a
 * long calculation.
 *
 * The patient wait is fully cancellable via the supplied
 * `AbortSignal` — the chat-side cancel button stays effective even
 * mid-wait. Progress events fire on the supplied `onPatientPhase`
 * callback so the chat row can keep the user informed during the
 * wait (otherwise the open just looks hung).
 *
 * Returns:
 *   - `ready=true` (with `reason: "ready" | "cleared"`) — proceed.
 *   - `ready=false` with `reason: "stuck_edit" | "stuck_busy"` —
 *      the user did not clear the host within the patient budget;
 *      surface the actionable error to chat.
 *   - `ready=false` with `reason: "probe_failed"` — bridge probe
 *      threw; fall through to the in-flight error catch.
 */
async function waitForTemplateHostReadyPatient(
  context: "top" | "pre-insert",
  options: {
    signal?: AbortSignal;
    onPatientPhase?: (
      phase: "hostReady:waiting:edit" | "hostReady:waiting:busy" | "hostReady:cleared",
      info: {
        context: "top" | "pre-insert";
        attempt: number;
        maxAttempts: number;
        reason: HostReadyReason;
        initialReason: HostReadyReason;
      }
    ) => void;
  } = {}
): Promise<PatientHostReadyResult> {
  const fast = await waitForTemplateHostReady();
  if (fast.ready || fast.reason === "probe_failed") {
    return { ...fast, initialReason: fast.reason };
  }

  const initialReason = fast.reason;
  console.warn(
    `[Excel] Template-open ${context} gate: host stuck (${initialReason}) after ` +
      `${fast.attempts} short-budget probes — entering patient wait (up to 5 minutes).`
  );

  const patient = await waitForHostReadyPatient({
    // Hand over our phase-1 result so the patient function doesn't
    // re-run the short gate (which would burn another ~1.4s of
    // virtual time + 5 host probes for no UX benefit — we already
    // know the host is stuck).
    initialResult: fast,
    signal: options.signal,
    onProgress: (info) => {
      const phase: "hostReady:waiting:edit" | "hostReady:waiting:busy" =
        info.initialReason === "stuck_edit"
          ? "hostReady:waiting:edit"
          : "hostReady:waiting:busy";
      options.onPatientPhase?.(phase, { context, ...info });
    },
  });
  if (patient.ready) {
    console.log(
      `[Excel] Template-open ${context} gate: host cleared during patient wait ` +
        `(initial=${initialReason}, attempts=${patient.attempts}).`
    );
    options.onPatientPhase?.("hostReady:cleared", {
      context,
      attempt: patient.attempts,
      maxAttempts: patient.attempts,
      reason: "cleared",
      initialReason,
    });
  }
  return { ...patient, initialReason };
}

/**
 * Run the full open-template chain against the host's intent bridge.
 * See the file-level docstring for the chain layout and the per-step
 * comments below for the rationale on each branch.
 */
export async function openTemplateInExcel(
  key: string,
  options?: OpenTemplateOptions
): Promise<OpenTemplateResult> {
  const startedAt = performance.now();
  let targetWorkbook = options?.targetWorkbook ?? "current";
  let retargetedFrom: "current" | undefined;
  let initialWorkbookName: string | undefined;
  let initialSheetName: string | undefined;
  if (targetWorkbook === "current") {
    try {
      const initialContext = await getActiveExcelContext();
      initialWorkbookName = initialContext.workbookName;
      initialSheetName = initialContext.activeSheet.name;
    } catch {
      // Best-effort focus restoration only. The import still targets the
      // active workbook according to the host's InsertWorksheetsFromBase64 contract.
    }
  }
  const onPhase = (phase: OpenTemplatePhase, data?: unknown) => {
    try {
      options?.onPhase?.(phase, data);
    } catch {
      // Observer bug — we don't let the caller's logging mistake
      // break the open.
    }
  };

  // ----- 0) Top host-ready gate -----
  //
  // Probe Excel's `isInEditMode` / `isBusy` flags BEFORE any host
  // write fires. If a prior op in this session left the cell-edit
  // lock dangling (or the user is mid-cell-edit), the very next
  // intent — `SetCalculationMode("manual")` inside the bracket
  // promise below — rejects with `InvalidOperationInCellEditMode`
  // and the rest of the chain cascades silent failures. Worse, the
  // bracket promise swallows the throw locally and the chain limps
  // on to `InsertWorksheetsFromBase64`, which then also rejects but
  // wraps as `OpenTemplateError("insert:error", ...)` so the user
  // sees "couldn't import the template sheets" — wrong root cause.
  //
  // Mirrors the gate at `excelService.ts::applyWorkbookModificationsViaIntents`;
  // see `services/factset/hostReady.ts` for the probe-and-wait
  // implementation. If the short gate fails we escalate to a
  // patient wait (up to 5 minutes) so the user can commit/cancel their
  // cell edit without losing the in-flight open — `onPhase` fires
  // `hostReady:waiting:*` events so the chat row keeps showing a
  // live status. On `probe_failed` we fall through and rely on the
  // in-flight error catch as the safety net — same posture as the
  // apply path. The patient wait coexists with main's retry-button
  // UX (PR #248): the patient gate handles the common transient
  // case in-place, and only escalates to the user-visible
  // `hostReadyRetryCopy({retried: true})` message after the full
  // 5-minute budget elapses.
  const topGate = await waitForTemplateHostReadyPatient("top", {
    signal: options?.signal,
    onPatientPhase: (phase, data) => onPhase(phase, data),
  });
  const topStuck = mapStuckReasonToPhaseAndMessage(topGate.reason);
  if (topStuck) {
    onPhase(topStuck.phase, {
      reason: topGate.reason,
      attempts: topGate.attempts,
      initialReason: topGate.initialReason,
      context: "top",
    });
    throw new OpenTemplateError(
      topStuck.phase,
      topStuck.message,
      undefined,
      {}
    );
  }
  if (topGate.reason === "cleared") {
    console.log(
      `[Excel] Template-open top gate: host cleared after ${topGate.attempts} attempts; proceeding.`
    );
  }

  // ----- 1+2) DownloadTemplate ‖ (read calc mode → flip to manual) -----
  //
  // Fired in parallel: download is a server-side fetch via the `OI
  // Remote Iframe` relay; the calc-mode bracket is `factset.Spreadsheet.*`
  // intents on the separate Spreadsheet relay, almost certainly on
  // different host queues, so doing them concurrently saves the ~2s
  // host-RPC cost of the bracket (it overlaps with the ~3.5s download).
  //
  // **Why we read the mode first.** We have to flip to manual to
  // bracket the structural mutations safely, but we must NOT
  // unconditionally restore to `"automatic"` on exit — if the user
  // had Excel pinned to manual mode before the open (a deliberate
  // choice for large models that recalc slowly), restoring to
  // automatic would silently overwrite their preference. So:
  //
  //   1. Read the user's current mode.
  //   2. If they're already in `"manual"`, the bracket is a no-op —
  //      skip both the flip and the restore. Mark `flipped = false`
  //      so callers can still tell the bracket "engaged" by virtue
  //      of being already-in-the-target-state.
  //   3. Otherwise flip to manual, remember the original mode, and
  //      restore to *that* on every exit path (success and error).
  //
  // `getCalculationMode` is wrapped to never reject — if the read
  // fails (host unreachable, weird state), we fall back to assuming
  // the bracket isn't already engaged and restore to `"automatic"`.
  // Worst case is the previous behavior, no regression.
  //
  // `calcBracketPromise` itself never rejects — every awaited site
  // is unconditionally safe and doesn't need its own try/catch.
  // The fence point is `addWorkbook` below: we MUST have manual
  // calc set before spawning the new workbook so it inherits the
  // setting.
  onPhase("download:start");
  onPhase("setCalc:manual:start");
  const calcBracketPromise: Promise<{
    flipped: boolean;
    originalMode: CalculationMode | null;
  }> = (async () => {
    let originalMode: CalculationMode | null = null;
    try {
      originalMode = await getCalculationMode();
      onPhase("setCalc:read:end", { mode: originalMode });
    } catch (e) {
      onPhase("setCalc:read:error", { error: errorString(e) });
    }
    if (originalMode === "manual") {
      onPhase("setCalc:manual:skipped", {
        reason: "user already in manual calc mode; preserving their setting",
      });
      return { flipped: false, originalMode };
    }
    try {
      await setCalculationMode("manual");
      onPhase("setCalc:manual:end");
      return { flipped: true, originalMode };
    } catch (e) {
      onPhase("setCalc:manual:error", { error: errorString(e) });
      return { flipped: false, originalMode };
    }
  })();

  // Pick the mode to restore the workbook to on chain exit. If we
  // never flipped (user was already in manual, or the flip threw),
  // there's nothing to restore — return `null` so callers skip the
  // restore call entirely. Otherwise prefer the captured original
  // mode (could be `"automatic"` or `"automaticExceptTables"`),
  // falling back to `"automatic"` only when the read itself failed.
  const calcRestoreTarget = (
    bracket: { flipped: boolean; originalMode: CalculationMode | null }
  ): CalculationMode | null => {
    if (!bracket.flipped) return null;
    return bracket.originalMode ?? "automatic";
  };

  // Drains the bracket promise (so we don't leak it past the
  // function lifetime) AND best-effort restores the original mode
  // if we flipped. Used by the early-exit error paths below
  // (download throw / download empty-bytes).
  const drainCalcAndRestore = async () => {
    const bracket = await calcBracketPromise;
    const target = calcRestoreTarget(bracket);
    if (target !== null) {
      try {
        await setCalculationMode(target);
      } catch {
        // Best-effort — primary error is already captured.
      }
    }
  };

  const templateInputs =
    options?.inputs && Object.keys(options.inputs).length > 0
      ? options.inputs
      : undefined;
  const instantiatedWithInputs = Boolean(templateInputs);
  let downloadResult: FactsetDownloadTemplate;
  try {
    if (templateInputs) {
      onPhase("refreshCalculate:start", { inputTypes: Object.keys(templateInputs) });
      const job = await refreshCalculate({ key, inputs: templateInputs });
      onPhase("refreshCalculate:poll", { refreshId: job.id, status: job.status });
      const finalStatus = await awaitRefreshCalculate(job.id, {
        timeoutMs: 5 * 60_000,
        pollTimeoutMs: 5 * 60_000,
      });
      onPhase("refreshCalculate:end", {
        refreshId: finalStatus.body.id || job.id,
        status: finalStatus.body.status,
      });
      const refreshResult = await getRefresh({ refreshId: finalStatus.body.id || job.id });
      onPhase("getRefresh:end", {
        hasBytes: Boolean(refreshResult.workbookBase64),
        base64Chars: refreshResult.workbookBase64?.length ?? 0,
        status: refreshResult.status,
        hostError: refreshResult.error ?? null,
      });
      downloadResult = {
        workbookBase64: refreshResult.workbookBase64,
        contentType: refreshResult.contentType,
        status: refreshResult.status,
        error: refreshResult.error,
        raw: refreshResult.raw,
      };
    } else {
      downloadResult = await downloadTemplate({ key });
    }
    onPhase("download:end", {
      hasBytes: Boolean(downloadResult.workbookBase64),
      base64Chars: downloadResult.workbookBase64?.length ?? 0,
      hostError: downloadResult.error ?? null,
      instantiatedWithInputs,
    });
  } catch (err) {
    onPhase("download:error", { error: errorString(err) });
    await drainCalcAndRestore();
    throw new OpenTemplateError(
      "download:error",
      `${templateInputs ? "RefreshCalculate" : "DownloadTemplate"} failed — ${errorString(err)}`,
      err
    );
  }

  if (downloadResult.error || !downloadResult.workbookBase64) {
    await drainCalcAndRestore();
    throw new OpenTemplateError(
      "download:error",
      downloadResult.error ??
        "DownloadTemplate returned no `workbookBase64` — host responded but with empty bytes.",
      undefined,
      { downloadResult }
    );
  }

  let bytesKb: number | undefined;
  try {
    bytesKb = Math.round(decodeBase64ByteLength(downloadResult.workbookBase64) / 1024);
  } catch {
    // Malformed base64 — let `InsertWorksheetsFromBase64` reject
    // with the host's verdict rather than failing the open here.
  }

  // Fence: AddWorkbook must run AFTER the calc-mode bracket has
  // settled so the new workbook inherits the manual-calc setting
  // (the whole point of the bracket). If the bracket promise is
  // still in flight at this point (download finished first), this
  // awaits it; if it already settled, this returns immediately.
  const calcBracket = await calcBracketPromise;
  const calcModeFlipped = calcBracket.flipped;

  const restoreCalcAfterPreInsertAbort = async (context: string) => {
    const restoreTarget = calcRestoreTarget(calcBracket);
    if (restoreTarget === null) return;
    onPhase("setCalc:auto:start", {
      context,
      target: restoreTarget,
    });
    try {
      await setCalculationMode(restoreTarget);
      onPhase("setCalc:auto:end");
    } catch (restoreErr) {
      onPhase("setCalc:auto:error", { error: errorString(restoreErr) });
    }
  };

  const retargetCurrentImportToNewWorkbook = (reason: string) => {
    if (targetWorkbook !== "current") return;
    console.warn(
      `[Excel] Retargeting template import to a new workbook: ${reason}`
    );
    targetWorkbook = "new";
    retargetedFrom = "current";
  };

  const assertNoIdentifierConflict = async (
    workbookName: string,
    targetLabel: string
  ) => {
    let hasIdentifierConflict = false;
    try {
      hasIdentifierConflict = await workbookHasNamedObject(
        workbookName,
        TEMPLATE_IDENTIFIER_NAME
      );
    } catch (err) {
      await restoreCalcAfterPreInsertAbort("named-object-preflight-failed");
      throw new OpenTemplateError(
        "insert:error",
        `I couldn't check whether ${targetLabel} already has a workbook-level name called \`${TEMPLATE_IDENTIFIER_NAME}\`. To avoid Excel's duplicate-name prompt, open the template in a new workbook or remove/rename the existing \`${TEMPLATE_IDENTIFIER_NAME}\` name before importing.`,
        err,
        { downloadResult, bytesKb, calcModeFlipped }
      );
    }

    if (!hasIdentifierConflict) return;

    await restoreCalcAfterPreInsertAbort("identifier-conflict");
    throw new OpenTemplateError(
      "insert:error",
      `${targetLabel} already has a workbook-level name called \`${TEMPLATE_IDENTIFIER_NAME}\`. Excel would show a duplicate-name prompt while importing this template. Open the template in a new workbook, or remove/rename the existing \`${TEMPLATE_IDENTIFIER_NAME}\` name before importing into this workbook.`,
      undefined,
      { downloadResult, bytesKb, calcModeFlipped }
    );
  };

  if (targetWorkbook === "current") {
    if (!initialWorkbookName) {
      retargetCurrentImportToNewWorkbook(
        "active workbook name was unavailable for Identifier preflight"
      );
    } else {
      try {
        const hasIdentifierConflict = await workbookHasNamedObject(
          initialWorkbookName,
          TEMPLATE_IDENTIFIER_NAME
        );
        if (hasIdentifierConflict) {
          if (options?.identifierConflictAction === "replaceExisting") {
            try {
              await removeNamedObject({
                name: TEMPLATE_IDENTIFIER_NAME,
                workbookName: initialWorkbookName,
                scope: -1,
              });
              console.warn(
                `[Excel] Removed existing ${TEMPLATE_IDENTIFIER_NAME} named object before template import per user confirmation.`
              );
            } catch (err) {
              await restoreCalcAfterPreInsertAbort("identifier-replace-failed");
              throw new OpenTemplateError(
                "insert:error",
                `I couldn't replace the existing workbook-level \`${TEMPLATE_IDENTIFIER_NAME}\` name before importing this template. Open the template in a new workbook instead.`,
                err,
                { downloadResult, bytesKb, calcModeFlipped }
              );
            }
          } else {
            retargetCurrentImportToNewWorkbook(
              `current workbook already has ${TEMPLATE_IDENTIFIER_NAME}`
            );
          }
        }
      } catch (err) {
        if (err instanceof OpenTemplateError) throw err;
        // Fail open into a fresh workbook rather than risk triggering
        // Excel's native duplicate-name modal in the active workbook.
        console.warn(
          "[Excel] Identifier preflight failed; retargeting template import to a new workbook:",
          err
        );
        retargetCurrentImportToNewWorkbook("Identifier preflight failed");
      }
    }
  }

  // ----- 3) AddWorkbook — optional. Default product behavior is to
  // insert templates into the user's active workbook; only spawn a
  // fresh workbook when the user explicitly asked for a separate file.
  // When requested, doing this as an explicit step (instead of via the
  // host's combined `InsertWorksheetsFromBase64({ addWorkbook: ... })`
  // mode) lets the new workbook inherit the manual calc mode set above.
  // -----
  if (targetWorkbook === "new") {
    onPhase("addWorkbook:start");
    try {
      const hostMessage = await addWorkbook();
      onPhase("addWorkbook:end", { hostMessage });
    } catch (err) {
      onPhase("addWorkbook:error", { error: errorString(err) });
      const restoreTarget = calcRestoreTarget(calcBracket);
      if (restoreTarget !== null) {
        try {
          await setCalculationMode(restoreTarget);
        } catch {
          // Best-effort — primary error wins.
        }
      }
      throw new OpenTemplateError(
        "addWorkbook:error",
        `AddWorkbook failed — ${errorString(err)}`,
        err,
        { downloadResult, bytesKb, calcModeFlipped }
      );
    }
  }

  // ----- 3b) Pre-insert host-ready gate -----
  //
  // Re-probe the host before the heaviest write in the chain. The
  // top gate above checked the host before the calc-bracket fired,
  // but `DownloadTemplate` is server-side and takes ~3-4s — plenty
  // of time for the user to click into a cell and start typing. If
  // they did, `InsertWorksheetsFromBase64` rejects with
  // `InvalidOperationInCellEditMode` and the user sees the
  // "couldn't import the template sheets" copy from the catch
  // below — actionable copy but with the wrong root cause attached
  // (they didn't break import; they entered edit mode).
  //
  // On stuck reasons, restore the calc mode (mirrors the existing
  // `insert:error` restore branch) and throw `OpenTemplateError`
  // carrying the full `{ downloadResult, bytesKb, calcModeFlipped }`
  // snapshot so the error log still records that the download
  // succeeded — useful diagnostic info even though the open didn't
  // complete. `probe_failed` falls through to the actual write and
  // lets the in-flight error catch act as the safety net. The
  // patient wait here is especially valuable: the 3-4s download
  // window is exactly when users tend to click into a cell to
  // "do something" while waiting for the import, so a transient
  // edit-mode flag is common at this gate.
  const preInsertGate = await waitForTemplateHostReadyPatient("pre-insert", {
    signal: options?.signal,
    onPatientPhase: (phase, data) => onPhase(phase, data),
  });
  const preInsertStuck = mapStuckReasonToPhaseAndMessage(preInsertGate.reason);
  if (preInsertStuck) {
    onPhase(preInsertStuck.phase, {
      reason: preInsertGate.reason,
      attempts: preInsertGate.attempts,
      initialReason: preInsertGate.initialReason,
      context: "pre-insert",
    });
    await restoreCalcAfterPreInsertAbort("host-ready-stuck-restore");
    throw new OpenTemplateError(
      preInsertStuck.phase,
      preInsertStuck.message,
      undefined,
      { downloadResult, bytesKb, calcModeFlipped }
    );
  }
  if (preInsertGate.reason === "cleared") {
    console.log(
      `[Excel] Template-open pre-insert gate: host cleared after ${preInsertGate.attempts} attempts; proceeding.`
    );
  }

  // ----- 3c) New-workbook identifier preflight -----
  //
  // When `targetWorkbook === "new"`, `AddWorkbook` above just spawned
  // a fresh workbook and (per Office.js) activated it. Re-query the
  // active context so we know the *new* workbook's name, then run
  // the same `Identifier` duplicate-name preflight we did for the
  // current-workbook path. A fresh workbook normally has no
  // workbook-level names, but the user could have a custom blank
  // template configured as their default — in which case Excel
  // would show the native duplicate-name modal during the
  // upcoming `InsertWorksheetsFromBase64` and the open would
  // stall waiting for human input.
  //
  // If we cannot determine the new workbook's name, abort with a
  // clear message rather than firing the import blind. In both
  // abort paths we restore the calc mode first so the user's
  // workbook is not left in manual-calc.
  let insertWorkbookName = initialWorkbookName;
  if (targetWorkbook === "new") {
    try {
      const targetContext = await getActiveExcelContext();
      insertWorkbookName = targetContext.workbookName;
    } catch {
      await restoreCalcAfterPreInsertAbort("missing-new-workbook-name");
      throw new OpenTemplateError(
        "insert:error",
        "I couldn't confirm which workbook Excel activated after creating the new workbook. To avoid Excel's duplicate-name prompt, select the new workbook and retry.",
        undefined,
        { downloadResult, bytesKb, calcModeFlipped }
      );
    }
    if (insertWorkbookName) {
      await assertNoIdentifierConflict(insertWorkbookName, "The target workbook");
    }
  }

  // ----- 4) InsertWorksheetsFromBase64 (no `addWorkbook` field) -----
  // Drops the template's sheets into the active workbook. If
  // `targetWorkbook === "new"`, the active workbook is the one we just
  // spawned above; otherwise it is the workbook the user was already in.
  if (targetWorkbook === "current" && initialWorkbookName && initialSheetName) {
    try {
      await activateWorksheet(initialSheetName, { workbookName: initialWorkbookName });
    } catch {
      // Best-effort: if this fails, fall back to the host's active-workbook behavior.
    }
  }
  onPhase("insert:start", {
    bytesKb,
    base64Chars: downloadResult.workbookBase64.length,
    calcModeFlipped,
    targetWorkbook,
    workbookName: insertWorkbookName,
    timeoutMs: INSERT_WORKSHEETS_TIMEOUT_MS,
  });
  let sheetNames: string[];
  try {
    sheetNames = await insertWorksheetsFromBase64(
      downloadResult.workbookBase64,
      undefined,
      INSERT_WORKSHEETS_TIMEOUT_MS
    );
    onPhase("insert:end", { sheetNames });
  } catch (err) {
    onPhase("insert:error", { error: errorString(err) });
    const restoreTarget = calcRestoreTarget(calcBracket);
    if (restoreTarget !== null) {
      onPhase("setCalc:auto:start", {
        context: "error-path-restore",
        target: restoreTarget,
      });
      try {
        await setCalculationMode(restoreTarget);
        onPhase("setCalc:auto:end");
      } catch (restoreErr) {
        onPhase("setCalc:auto:error", { error: errorString(restoreErr) });
      }
    }
    throw new OpenTemplateError(
      "insert:error",
      `InsertWorksheetsFromBase64 failed — ${errorString(err)}`,
      err,
      { downloadResult, bytesKb, calcModeFlipped }
    );
  }

  // ----- 5) Cleanup (parallel) -----
  //
  // Two best-effort housekeeping steps fired CONCURRENTLY:
  //
  //   a) `ActivateWorksheet(sheetNames[0])` — pin the active sheet
  //      to the first imported sheet (typically the template's
  //      Cover). Empirically the host already lands here, but
  //      making it explicit guarantees a deterministic landing tab.
  //
  //   b) `DeleteWorksheet("Sheet1")` — remove the leftover empty
  //      default sheet that `AddWorkbook` spawned, but ONLY when
  //      the template's imported sheet list does NOT itself contain
  //      `"Sheet1"`. The host's name-collision behavior on
  //      `InsertWorksheetsFromBase64` is not pre-defined for our
  //      side: it MAY rename the imported sheet to `"Sheet1 (2)"`
  //      and keep our empty default as `"Sheet1"` (safe — we delete
  //      the empty default), but it MAY also keep the imported
  //      sheet as `"Sheet1"` and rename the empty default (in which
  //      case `deleteWorksheet("Sheet1")` would delete the
  //      template's data sheet — silent data loss). Error
  //      swallowing only protects against failed deletes, not
  //      successful wrong-sheet deletes, so we guard at the call
  //      site instead. When skipped, the leftover empty default
  //      sheet stays in the workbook (cosmetic only); when
  //      executed, we know the imported sheets aren't named
  //      `"Sheet1"` so the delete can only land on the empty
  //      default.
  //
  // **Why parallel.** Both are independent of each other and
  // converge to the same end state — "Cover active, Sheet1 gone" —
  // regardless of host execution order. Both ~1s host-RPC calls
  // so the parallelism saves ~1s of wall-clock. Each promise
  // swallows its own error so `Promise.all` never rejects — neither
  // failure can derail an otherwise-successful open.
  //
  // Both run BEFORE `SetCalculationMode("automatic")` so any recalc
  // that fires on workbook restructure stays bracketed by the
  // manual-calc setting.
  const coverSheet = sheetNames[0];
  const cleanupPromises: Promise<void>[] = [];

  if (coverSheet) {
    const activateOptions = insertWorkbookName
      ? { workbookName: insertWorkbookName }
      : undefined;
    onPhase("activate:cover:start", {
      worksheetName: coverSheet,
      workbookName: insertWorkbookName,
    });
    cleanupPromises.push(
      activateWorksheet(coverSheet, activateOptions)
        .then(() => {
          onPhase("activate:cover:end");
        })
        .catch((e: unknown) => {
          onPhase("activate:cover:error", { error: errorString(e) });
        })
    );
  }

  // Guard the delete against templates that ship a `Sheet1` of their
  // own. See the rationale block above the cleanup section: without
  // this check we could silently delete the template's data when the
  // host's name-collision resolution keeps the imported sheet's
  // original name and renames our empty default instead.
  if (targetWorkbook === "new" && !sheetNames.includes("Sheet1")) {
    onPhase("delete:sheet1:start");
    cleanupPromises.push(
      deleteWorksheet("Sheet1")
        .then(() => {
          onPhase("delete:sheet1:end");
        })
        .catch((e: unknown) => {
          onPhase("delete:sheet1:error", { error: errorString(e) });
        })
    );
  } else if (targetWorkbook === "new") {
    onPhase("delete:sheet1:skipped", {
      reason: "template ships a sheet named Sheet1; preserving template data",
    });
  } else {
    onPhase("delete:sheet1:skipped", {
      reason: "template inserted into current workbook; preserving existing Sheet1",
    });
  }

  await Promise.all(cleanupPromises);

  // ----- 6) SetCalc(restore-target) — restore the workbook to the
  // user's pre-open calc mode on exit. Awaited (not fire-and-
  // forget) for correctness: if the user kicks off another intent
  // immediately after this returns, we don't want them racing
  // against an unrestored manual mode.
  //
  // The target is the mode we captured BEFORE flipping to manual
  // (typically `"automatic"`, but could be `"automaticExceptTables"`
  // for users who configured Excel that way), NOT a hardcoded
  // `"automatic"` — restoring to `"automatic"` unconditionally
  // would silently flip a user from `"automaticExceptTables"` to
  // `"automatic"` after every template open. If the original-mode
  // read failed back at the start of the chain, we fall back to
  // `"automatic"` (no regression vs. the previous behavior). If
  // we never flipped (user was already in manual), `restoreTarget`
  // is `null` and we skip the restore call entirely.
  const restoreTarget = calcRestoreTarget(calcBracket);
  if (restoreTarget !== null) {
    onPhase("setCalc:auto:start", {
      context: "success-path-restore",
      target: restoreTarget,
    });
    try {
      await setCalculationMode(restoreTarget);
      onPhase("setCalc:auto:end");
    } catch (restoreErr) {
      onPhase("setCalc:auto:error", { error: errorString(restoreErr) });
    }
  }

  // ----- 7) HarmonizedRefreshWorksheets — the missing step that
  // makes the difference between "template is open with stale /
  // `#N/A` cells" and "template is open with live FactSet data".
  //
  // **Why we pass an explicit `sheetNames` list instead of
  // letting the wrapper send `fdc3.nothing`.** Per FactSet's
  // `excel-tools.md`, `fdc3.nothing` refreshes the currently
  // selected sheet(s) only — NOT the entire workbook. Inactive
  // tabs don't fetch their FDS data until they're activated (or
  // refreshed by name), so a bare `fdc3.nothing` would leave them
  // stale.
  // The user-visible symptom is exactly that: open the template,
  // Cover lands populated, click any other tab → cells are stale
  // until they hit the host's refresh button manually. Passing
  // the full `sheetNames` list from `InsertWorksheetsFromBase64`
  // forces the host to fetch every imported sheet's data
  // up-front so tab switching is instant. We deliberately exclude
  // the leftover `Sheet1` (already deleted in step 5b best-effort,
  // and empty even if the delete failed) — refreshing it would
  // be a no-op since it carries no FDS formulas.
  //
  // Why it sits AFTER `setCalc("automatic")` instead of inside
  // the manual-calc bracket:
  //   - The host's refresh primitive writes FDS values into cells
  //     directly; once they land, Excel's calc engine cascades the
  //     dependent plain-Excel formulas (sums, ratios, growth %) on
  //     top. With calc still pinned manual we'd block that
  //     cascade and force the user to hit F9 / change a cell to
  //     see the derived numbers. So the order is: bracket the
  //     structural mutations (AddWorkbook + insert) under manual
  //     calc → restore automatic → refresh data → Excel cascades
  //     dependent formulas naturally.
  //   - Refresh is the heaviest step in the whole chain (FactSet-
  //     side network fetches across every FDS / FDSC / FDSZ cell
  //     across every imported sheet). Pinning it last keeps the
  //     visible workbook open as soon as the structural import is
  //     done — the host's own progress overlay
  //     (`disableProgressIndicator: false`, the default) covers
  //     the data-fetch latency, and the chat handler runs its own
  //     `loadingStatus` line in parallel.
  //   - The whole step is best-effort: a hard host error surfaces
  //     `refresh: "failed"`, our soft timeout (host RPC still in
  //     flight) surfaces `refresh: "in_progress"`, and the open is
  //     successful in all three cases — the workbook is open and
  //     visible regardless. The chat handler picks the right
  //     messaging from the discriminator.
  let refresh: OpenTemplateResult["refresh"] = "skipped";
  const wantsRefresh = options?.refresh ?? true;
  if (wantsRefresh) {
    // Defensive: if the host returned no sheet names (shouldn't
    // happen on a successful insert, but `insertWorksheetsFromBase64`'s
    // contract leaves the array non-null but possibly empty), fall
    // back to the `fdc3.nothing` shape rather than sending
    // `sheetNames: []` — the host might interpret that as "refresh
    // nothing". The fallback is no worse than the old behavior.
    const refreshOptions =
      sheetNames.length > 0 ? { sheetNames } : undefined;
    onPhase("refresh:start", {
      timeoutMs: REFRESH_TIMEOUT_MS,
      sheetNames: refreshOptions?.sheetNames ?? null,
    });
    try {
      const hostMessage = await refreshWorksheets(
        refreshOptions,
        REFRESH_TIMEOUT_MS
      );
      refresh = "completed";
      onPhase("refresh:end", {
        hostMessage,
        sheetCount: refreshOptions?.sheetNames?.length ?? 0,
      });
    } catch (refreshErr) {
      // Discriminate timeout (host still working in the background)
      // from a real host error. On timeout we report
      // `refresh: "in_progress"` so chat doesn't warn the user
      // that refresh failed when the host's progress dialog is
      // still actively refreshing.
      if (isIntentTimeoutError(refreshErr)) {
        refresh = "in_progress";
        onPhase("refresh:in-progress", {
          error: errorString(refreshErr),
          timeoutMs: REFRESH_TIMEOUT_MS,
        });
      } else {
        refresh = "failed";
        onPhase("refresh:error", { error: errorString(refreshErr) });
      }
    }
  } else {
    onPhase("refresh:skipped", { reason: "options.refresh === false" });
  }

  // Drain any stuck cell-edit flag the open chain may have left
  // behind. `InsertWorksheetsFromBase64` + `HarmonizedRefreshWorksheets`
  // are both structural writes that occasionally leave the host's
  // `isInEditMode` set even though no actual cell-edit is in flight
  // — which then traps the NEXT op (e.g. a follow-up template open)
  // at its pre-apply gate. Best-effort + non-throwing; see
  // `hostReady.ts::drainEditModeAfterWrite` for the probe + force-
  // commit semantics.
  try {
    const drainResult = await drainEditModeAfterWrite();
    if (drainResult === "forced_commit") {
      console.log(
        "[Excel] Post-open drain: forced commit of stuck edit-mode flag."
      );
    }
  } catch (e) {
    console.warn(
      "[Excel] Post-open edit-mode drain threw unexpectedly; ignoring:",
      e
    );
  }

  return {
    sheetNames,
    bytesKb,
    calcModeFlipped,
    durationMs: Math.round(performance.now() - startedAt),
    downloadResult,
    refresh,
    instantiatedWithInputs,
    targetWorkbook,
    ...(retargetedFrom ? { retargetedFrom } : {}),
  };
}

function errorString(e: unknown): string {
  return e instanceof Error ? `${e.name}: ${e.message}` : String(e);
}

/**
 * Decode a base64 string's BYTE length without materialising the
 * decoded payload. The standard base64 grammar is exactly 4 input
 * characters per 3 output bytes, so once the alphabet check passes
 * the byte count is `chars * 3 / 4 - paddingChars`, which is O(1)
 * regardless of size. A 340KB template (~453KB base64) used to
 * round-trip through `atob(...).length` here, briefly allocating
 * ~680KB of UTF-16 string memory just to read the `.length` — a
 * noticeable spike inside FactSet Workstation's embedded Chromium.
 *
 * The alphabet regex still catches malformed payloads (it throws
 * the same way `atob` would) so callers don't silently mis-report
 * a size for garbled bytes. Standard alphabet only — URL-safe
 * variants don't appear on the FactSet wire today.
 */
function decodeBase64ByteLength(base64: string): number {
  const len = base64.length;
  if (len === 0) return 0;
  if (len % 4 !== 0 || !/^[A-Za-z0-9+/]*={0,2}$/.test(base64)) {
    throw new Error("decodeBase64ByteLength: malformed base64 payload");
  }
  let pad = 0;
  if (base64.charCodeAt(len - 1) === 61 /* '=' */) pad++;
  if (base64.charCodeAt(len - 2) === 61) pad++;
  return (len * 3) / 4 - pad;
}
